import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { validate as isUuid } from 'uuid'
import { useToast } from '@winnin/winnin_ui.components.toast'

import type { GetTerritoryNicheContextsQuery } from 'generated/graphql'
import { useNicheMutations, useNicheQueries } from 'hooks-queries/niche'
import { customerioTracker } from 'lib'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import {
  CREATE_TERRITORY_NICHE_CONTEXT_HASH_LOG,
  CREATE_TERRITORY_NICHE_CONTEXT_LOG,
  DELETE_TERRITORY_NICHE_CONTEXT_LOG,
  GET_TERRITORY_NICHE_CONTEXTS_LOG,
  MAX_CUSTOM_CONTEXTS,
} from './use-niche-context-manager.consts'
import type { IUseNicheContextManager, TUseNicheContextManagerReturn } from './use-niche-context-manager.types'
import { getDefaultContexts } from './use-niche-context-manager.utils'
import type { Context } from '../../types'

export function useNicheContextManager({
  territoryId,
  skipDefaultContexts,
  skipContextsQuery,
  initialContextId,
  customDefaultContexts,
  territoryIdResolver,
  readinessCheck,
  hideCreateContext = false,
  hideDelete,
  featureType,
  excludeContextId,
  deferAutoSelect,
  tabName = 'Niches',
  pageName = 'Topics page',
}: IUseNicheContextManager): TUseNicheContextManagerReturn {
  const hasInitialized = useRef(false)
  const prevTerritoryRef = useRef(territoryId)
  const prevContextsRef = useRef(customDefaultContexts)
  // Dedupe key (`territoryId:contextId`) of the last context a hash was requested for.
  // Set inside `createHashAndUpdate` (so every caller updates it) and read by the
  // auto-select effect to avoid firing a duplicate createNicheContextHash.
  const lastHashRequestRef = useRef<string | null>(null)
  const baseContexts = getDefaultContexts(customDefaultContexts)
  const hasReadinessCheck = Boolean(readinessCheck)
  const isReady = readinessCheck?.isReady !== false
  const t = useTranslate()
  const { createToast } = useToast()

  const resolveTerritoryId = useCallback(
    (contextId: string): string => territoryIdResolver?.(contextId) ?? territoryId,
    [territoryIdResolver, territoryId]
  )

  const defaultContexts = useMemo<Context[]>(() => {
    if (skipDefaultContexts) {
      return []
    }
    return baseContexts.map(({ translation, ...rest }) => ({
      ...rest,
      name: t(translation),
    }))
  }, [skipDefaultContexts, baseContexts, t])

  const [activeContext, setActiveContext] = useState<Context>(() => {
    if (initialContextId) {
      const match = defaultContexts.find(ctx => ctx.id === initialContextId)
      if (match) return { ...match }
    }
    return { ...defaultContexts[0] }
  })
  const [contexts, setContexts] = useState<Context[]>(defaultContexts)
  const [createNicheError, setCreateNicheError] = useState(null)

  const currentLanguage = useCurrentLanguage()

  useEffect(() => {
    const currentActiveContext = contexts.find(item => item.id === activeContext?.id)
    if (!currentActiveContext || !activeContext) {
      return
    }
    if (currentActiveContext.id === activeContext?.id && currentActiveContext.name !== activeContext?.name) {
      setActiveContext(currentActiveContext)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage])

  const isDefaultContext = useMemo(() => {
    return baseContexts.some(ctx => ctx.id === activeContext.id)
  }, [activeContext.id, baseContexts])

  const [deleteNicheContext, { loading: isDeleting }] = useNicheMutations({
    mutationName: 'deleteTerritoryNicheContext',
    errorLog: DELETE_TERRITORY_NICHE_CONTEXT_LOG,
  })

  const [createNicheContext, { loading: isCreating }] = useNicheMutations({
    mutationName: 'createTerritoryNicheContext',
    errorLog: CREATE_TERRITORY_NICHE_CONTEXT_LOG,
  })

  const [createNicheContextHash, { loading: isGeneratingHash, error }] = useNicheMutations({
    mutationName: 'createNicheContextHash',
    errorLog: CREATE_TERRITORY_NICHE_CONTEXT_HASH_LOG,
  })

  const updateContextHash = useCallback((contextId: string, hash: string) => {
    setContexts(prevContexts => prevContexts.map(ctx => (ctx.id === contextId ? { ...ctx, hash } : ctx)))
    setActiveContext(prev => (prev.id === contextId ? { ...prev, hash } : prev))
  }, [])

  const createHashAndUpdate = useCallback(
    (contextId: string, contextValue: string | null) => {
      const correctTerritoryId = resolveTerritoryId(contextId)
      // Record what we're hashing so the auto-select effect never fires a duplicate
      // request for the same territory + context — regardless of which path triggered
      // this one (auto-select, chip click, retry, create).
      lastHashRequestRef.current = `${correctTerritoryId}:${contextId}`
      const input = {
        territoryId: correctTerritoryId,
        ...(isUuid(contextId) ? { contextId } : { context: contextValue }),
      }
      createNicheContextHash({
        variables: { input },
      })
        .then(({ data }) => {
          const hash = data?.createNicheContextHash?.hash
          if (hash) {
            updateContextHash(contextId, hash)
          }
        })
        .catch(error => {
          setCreateNicheError(error)
        })
    },
    [createNicheContextHash, resolveTerritoryId, updateContextHash]
  )

  const handleRetrySelectContext = useCallback(() => {
    setCreateNicheError(null)
    createHashAndUpdate(activeContext.id, activeContext.name)
  }, [activeContext, createHashAndUpdate])

  useEffect(() => {
    if (hasReadinessCheck && !isReady) return
    if (hasInitialized.current) return
    if (skipDefaultContexts && !initialContextId) return

    const startContextId = initialContextId ?? baseContexts[0].id
    const isFirstDefault = startContextId === baseContexts[0].id
    const contextValue = isFirstDefault ? null : (defaultContexts.find(ctx => ctx.id === startContextId)?.name ?? null)
    createHashAndUpdate(startContextId, contextValue)
    hasInitialized.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady])

  useEffect(() => {
    if (!hasInitialized.current) return

    const territoryChanged = prevTerritoryRef.current !== territoryId
    const contextsChanged = prevContextsRef.current !== customDefaultContexts

    prevTerritoryRef.current = territoryId
    prevContextsRef.current = customDefaultContexts

    if (!territoryChanged && !contextsChanged) return

    // The stored hashes are territory-specific and get cleared below, so any dedupe key
    // recorded for the previous territory is stale. Reset it — otherwise returning to a
    // category after a detour that never re-hashed (e.g. an empty category) would match
    // the old key and the auto-select effect would skip re-hashing, leaving the active
    // chip selected but the tab stuck on the loading skeleton. Branches that hash below
    // repopulate it via `createHashAndUpdate`.
    lastHashRequestRef.current = null

    if (contextsChanged) {
      const newFirst = defaultContexts[0]
      setActiveContext({ ...newFirst, hash: null })
      createHashAndUpdate(newFirst.id, null)
    } else if (skipDefaultContexts) {
      // API-driven contexts (category pages): hashes are territory-specific, so clear
      // every stored hash. A context shared with the previous category (promoted <->
      // parent) is kept by mergeContexts, and handleActiveContext skips re-hashing when
      // a hash is present — so without this it would reuse the previous territory's hash.
      // The auto-select effect below re-hashes the active context; the rest re-hash on
      // demand when selected. (Hashing baseContexts[0] here would target the system
      // `happeningNow` context, which is not shown on these tabs.)
      setContexts(prevContexts => prevContexts.map(ctx => ({ ...ctx, hash: null })))
      setActiveContext(prev => ({ ...prev, hash: null }))
    } else {
      setActiveContext(prev => ({ ...prev, hash: null }))
      createHashAndUpdate(baseContexts[0].id, null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId, customDefaultContexts])

  const cacheKey = useMemo(
    () => `listTerritoryNicheContextsPaginated({"filters":{"territoryId":"${territoryId}"}})`,
    [territoryId]
  )

  const { data, loading } = useNicheQueries({
    queryName: 'getTerritoryNicheContexts',
    queryVariables: {
      filters: {
        territoryId,
      },
    },
    errorLog: GET_TERRITORY_NICHE_CONTEXTS_LOG,
    options: {
      skip: skipContextsQuery || !isReady || !territoryId,
    },
  })

  const handleCloseContext = useCallback(
    (id: string) => {
      setContexts(currentContexts => {
        const context = currentContexts.find(c => c.id === id)
        const { NICHE_CONTEXT } = analyticsTracker.eventNames
        analyticsTracker.eventTrack(NICHE_CONTEXT.DELETE, {
          'Page title': 'Topics page',
          Tab: 'Niches',
          'Context niche name': context.name,
        })

        deleteNicheContext({
          variables: { input: { id } },
          onError: () => {
            createToast({
              content: t(TOLGEE_GLOBALS.somethingWentWrong),
            })
          },
          onCompleted: () => {
            if (id === activeContext.id) {
              setActiveContext(currentContexts[0])
            }
          },
          update: (cache, { data }) => {
            if (data?.deleteTerritoryNicheContext?.success) {
              cache.modify({
                fields: {
                  [cacheKey]: (
                    existingData: GetTerritoryNicheContextsQuery['listTerritoryNicheContextsPaginated'] | undefined,
                    { readField }
                  ) => {
                    return {
                      ...existingData,
                      data: existingData.data.filter(contextRef => readField('id', contextRef) !== id),
                      totalCount: Math.max(0, (existingData.totalCount || 0) - 1),
                    }
                  },
                },
              })
            }
          },
        })
        return currentContexts
      })
    },

    [activeContext.id, deleteNicheContext, cacheKey, createToast, t]
  )

  const apiContexts = useMemo(() => {
    const contextData = data?.listTerritoryNicheContextsPaginated?.data
    if (!contextData) return []

    return contextData
      .slice()
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .filter(nicheContext => nicheContext.id !== excludeContextId)
      .map((nicheContext): Context => {
        const shouldShowClose = !nicheContext.isGlobal && !hideDelete
        return {
          id: nicheContext.id,
          name: nicheContext.context,
          icon: null,
          hash: null,
          ...(shouldShowClose && { onClose: () => handleCloseContext(nicheContext.id) }),
        }
      })
  }, [data?.listTerritoryNicheContextsPaginated?.data, handleCloseContext, hideDelete, excludeContextId])

  const mergeContexts = useCallback(
    (prev: Context[]) => {
      const combined = [...defaultContexts, ...apiContexts]
      return combined.map(c => {
        const existing = prev.find(p => p.id === c.id)
        return { ...c, hash: existing?.hash || c.hash }
      })
    },
    [defaultContexts, apiContexts]
  )

  useEffect(() => {
    setContexts(mergeContexts)
  }, [mergeContexts])

  useEffect(() => {
    // Wait until the caller has resolved `excludeContextId` (see `deferAutoSelect`)
    // so we never auto-select / hash a context that is about to be filtered out.
    if (deferAutoSelect) return
    if (!skipDefaultContexts || !apiContexts.length) return

    // Pick the context that should be active for the current territory:
    // - the active one if it still belongs to this territory's contexts (e.g. switching
    //   from a promoted category to its parent, which share the same contexts);
    // - otherwise the first context — first load, or a category change to an unrelated
    //   category. `apiContexts` is already filtered by `excludeContextId`, so
    //   `apiContexts[0]` is the correct promoted-aware chip.
    const isActiveContextPresent = apiContexts.some(context => context.id === activeContext?.id)
    const target = isActiveContextPresent ? activeContext : apiContexts[0]

    if (target.id !== activeContext?.id) {
      setActiveContext(target)
    }
    hasInitialized.current = true

    // Ensure the target context has a hash for the current territory. The hash is
    // territory-specific and is cleared on every territory change, so a still-present
    // context (promoted → parent) needs a fresh one — otherwise the chip stays selected
    // but the tab is stuck on the loading skeleton forever. Dedupe so we never fire a
    // duplicate request while one is already in flight for the same territory + context.
    if (!target.hash) {
      // `createHashAndUpdate` records the request key; only skip when it already matches
      // (a request for this territory + context is in flight or done — e.g. fired by a
      // chip click via `handleActiveContext`). Resolve the territory the same way so the
      // key matches when a `territoryIdResolver` maps the context elsewhere.
      const requestKey = `${resolveTerritoryId(target.id)}:${target.id}`
      if (lastHashRequestRef.current !== requestKey) {
        createHashAndUpdate(target.id, target.name)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiContexts, createHashAndUpdate, skipDefaultContexts, deferAutoSelect, activeContext?.id, activeContext?.hash])

  const hasReachedLimit = useMemo(() => {
    return apiContexts.length >= MAX_CUSTOM_CONTEXTS
  }, [apiContexts])

  const handleActiveContext = useCallback(
    async (id: string) => {
      const context = contexts.find(c => c.id === id)
      if (!context) return

      const { NICHE_CONTEXT } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(NICHE_CONTEXT.SELECT, {
        'Page title': pageName,
        Tab: tabName,
        'Context niche name': context.name,
      })

      setActiveContext(context)

      if (!context.hash) {
        const isDefault = context.id === baseContexts[0].id
        const contextValue = isDefault ? null : context.name
        createHashAndUpdate(id, contextValue)
      }
    },
    [contexts, createHashAndUpdate, baseContexts, tabName, pageName]
  )

  const handleNewContextActivation = useCallback(
    async (contextId: string, contextName: string | null) => {
      const newCtx = contexts.find(c => c.id === contextId) ?? {
        id: contextId,
        name: contextName ?? '',
        icon: null,
        hash: null,
        onClose: handleCloseContext,
      }
      setActiveContext(newCtx)

      createHashAndUpdate(contextId, contextName)
    },
    [contexts, createHashAndUpdate, handleCloseContext]
  )

  const handleCreateContext = useCallback(
    async (name: string) => {
      if (hasReachedLimit) return
      createNicheContext({
        variables: { input: { context: name, territoryId } },
        onError: () => {
          createToast({
            content: t(TOLGEE_GLOBALS.somethingWentWrong),
          })
        },
        onCompleted: async ({ createTerritoryNicheContext }) => {
          const newContextId = createTerritoryNicheContext?.data.id
          if (newContextId) {
            const { NICHE_CONTEXT } = analyticsTracker.eventNames
            analyticsTracker.eventTrack(NICHE_CONTEXT.ADD, {
              'Page title': 'Topics page',
              Tab: 'Niches',
              'Context niche name': name,
            })
            if (featureType) {
              customerioTracker.track(customerioTracker.eventNames.ADD_NICHE_CONTEXT, {
                feature: featureType,
                action: 'create_context',
                tab: 'niches',
                name,
              })
            }
            await handleNewContextActivation(newContextId, name)
          }
        },
        update: (cache, { data }) => {
          if (data?.createTerritoryNicheContext?.data) {
            cache.modify({
              fields: {
                [cacheKey]: (
                  existingData: GetTerritoryNicheContextsQuery['listTerritoryNicheContextsPaginated'] | undefined
                ) => ({
                  ...existingData,
                  data: [...existingData.data, data.createTerritoryNicheContext.data],
                  totalCount: (existingData.totalCount || 0) + 1,
                }),
              },
            })
          }
        },
      })
    },
    [
      hasReachedLimit,
      createNicheContext,
      territoryId,
      createToast,
      t,
      featureType,
      handleNewContextActivation,
      cacheKey,
    ]
  )

  return {
    contexts,
    isLoadingContext: loading || isDeleting || isCreating || Boolean(deferAutoSelect),
    isGeneratingHash,
    hideCreateContext,
    activeContext,
    isDefaultContext,
    hasReachedLimit,
    handleActiveContext,
    handleCreateContext,
    error: error,
    createNicheError,
    handleRetrySelectContext,
  }
}
